/**
 * Learning Center API: the onboarding checklist, the whole surface (plan
 * docs/plans/lms-plan.md 5.7, 12, Appendix A "Onboarding", 18.24; WP-38's
 * routes, WP-47's client; R1, R14). Typed calls over services/api (the shared
 * axios instance that adds the JWT and X-Site-Id); nothing under src/lms
 * imports axios (2.3a). WP-41's api/me.ts keeps its small getMyOnboarding for
 * Home's card; the Onboarding tab and the admin screens use this file.
 *
 * The hire (12.3):
 *   GET  /me/onboarding                          -> { run, items, progress } | null (the active run in this network)
 *   POST /me/onboarding/items/:id/done           JSON { evidence_text? }, or multipart (file, evidence_text?,
 *                                                issued_on?, expires_on?) when the item takes a file -> item
 *   POST /me/onboarding/items/:id/not-applicable { reason } -> item
 *   GET  /me/onboarding/items/:id/evidence-url   ?disposition= -> { url, filename }
 *
 * The admin, templates (12.1):
 *   GET   /admin/onboarding/templates            -> { items: [template + item_count], page } (the default first)
 *   POST  /admin/onboarding/templates            { name, description?, is_default? } -> template (201);
 *                                                409 LMS_CONFLICT { existing_template: { id, is_active } }
 *   GET   /admin/onboarding/templates/:id        -> { template, items, retired_items }
 *   PATCH /admin/onboarding/templates/:id        { name?, description?, is_default?, is_active? } -> template
 *   PUT   /admin/onboarding/templates/:id/items  { items: [...] } (the whole ordered list; ids kept when
 *                                                present; an item left out is retired) -> { items, retired_items }
 *
 * The admin, runs (12.2 to 12.4):
 *   GET  /admin/onboarding/runs                  ?status=active|completed|cancelled|all&user_id=&site_id=
 *                                                -> { items: [run + user, template, site, progress], page }
 *   POST /admin/onboarding/runs                  { user_id, template_id, site_id, start_date } (201)
 *                                                -> { run, items, progress, assignments };
 *                                                409 { active_run_id } for a second active run
 *   GET  /admin/onboarding/runs/:id              -> { run, items, progress, user, started_by, site, template }
 *   POST /admin/onboarding/runs/:id/cancel       { reason } -> { run, assignments_cancelled }
 *   POST /admin/onboarding/runs/:id/items/:itemId/done            as the hire's route, admin items only
 *   POST /admin/onboarding/runs/:id/items/:itemId/not-applicable  { reason } (any item)
 *   POST /admin/onboarding/runs/:id/items/:itemId/verify          -> item
 *   PUT  /admin/onboarding/runs/:id/items/:itemId/note            { note } (null clears it) -> item
 *   GET  /admin/onboarding/runs/:id/items/:itemId/evidence-url    ?disposition= -> { url, filename }
 *
 * Error codes (18.24): 403 LMS_ONBOARDING_ADMIN_ITEM (a hire on an admin
 * item), 403 LMS_ONBOARDING_HIRE_ITEM (the admin done route on a hire's item),
 * 409 LMS_COURSE_ITEM (a manual done on a course item, or a hire marking one
 * not applicable), 403 LMS_ONBOARDING_SELF_VERIFY; text that looks like a
 * credential is a 400 carrying { rule, field } (G14, 5.7).
 *
 * Query strings travel as axios `params`, so the URL itself stays the path
 * (the test harness routes GETs by exact URL). A multipart body is a FormData
 * posted with no explicit Content-Type: the browser sets the boundary itself.
 */
import api from '../../services/api';
import type { LmsList } from './client';
import type { MyOnboardingProgress } from './me';

const enc = encodeURIComponent;
const BASE = '/api/lms';

// ---------------------------------------------------------------------------
// Vocabulary (5.7; backend repo/spec.js)
// ---------------------------------------------------------------------------

/** 5.7 lms_onboarding_items.kind; 12.1 says what each one covers. */
export type OnboardingItemKind = 'external_training' | 'account_setup' | 'document' | 'course' | 'meeting' | 'custom';

export const ONBOARDING_ITEM_KINDS: readonly OnboardingItemKind[] = [
  'external_training',
  'account_setup',
  'document',
  'course',
  'meeting',
  'custom',
];

/** 5.7 responsible: the new hire does it, or an admin does. */
export type OnboardingResponsible = 'new_hire' | 'admin';

/** 5.7 evidence: nothing, a written confirmation, or a file. */
export type OnboardingEvidence = 'none' | 'text' | 'file';

export type OnboardingItemStatus = 'pending' | 'done' | 'not_applicable';

export type OnboardingRunStatus = 'active' | 'completed' | 'cancelled';

/** The ?status= filter of GET /admin/onboarding/runs (default active). */
export type OnboardingRunFilter = OnboardingRunStatus | 'all';

/**
 * The bounds services/lms/onboarding/validators.js holds (WP-38 LIMITS); the
 * server refuses anything past them, so the forms stop there first.
 */
export const ONBOARDING_LIMITS = Object.freeze({
  templateName: 120,
  templateDescription: 2000,
  itemTitle: 200,
  itemDescription: 5000,
  url: 2000,
  dueDaysMax: 365,
  itemsPerTemplate: 200,
  evidenceText: 2000,
  reason: 1000,
});

/** 5.7 item defaults (validators.js ITEM_DEFAULTS). */
export const ONBOARDING_ITEM_DEFAULTS = Object.freeze({
  evidence: 'none' as OnboardingEvidence,
  due_days: 14,
  requires_verification: false,
});

/** 12.1: only a course or an external training links a course (validators.js COURSE_LINK_KINDS). */
export const COURSE_LINK_KINDS: readonly OnboardingItemKind[] = ['course', 'external_training'];

/** 12.3 and 2.3a: evidence files are a PDF, PNG or JPEG of at most 10 MB. */
export const EVIDENCE_MIMES = ['application/pdf', 'image/png', 'image/jpeg'] as const;
export const EVIDENCE_MAX_BYTES = 10 * 1024 * 1024;

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

/** 18.24: progress = { total, done, not_applicable, pending, awaiting_verification, overdue }. */
export type OnboardingProgress = MyOnboardingProgress;

/** 5.7 lms_onboarding_runs. */
export interface OnboardingRun {
  id: string;
  user_id: string;
  template_id: string;
  network_id: string;
  site_id: string;
  /** The hire's first day (YYYY-MM-DD); due dates count from it (12.2). */
  start_date: string;
  started_by: string;
  started_at: string;
  status: OnboardingRunStatus;
  completed_at: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  created_at?: string;
}

/**
 * 5.7 lms_onboarding_run_items as a response carries one (18.24): the row
 * without storage_key, plus has_evidence_file. The eight columns copied from
 * the template item at run start keep a hire's history fixed.
 */
export interface OnboardingRunItem {
  id: string;
  run_id: string;
  item_id: string;
  ordinal: number;
  title: string;
  description_md: string | null;
  kind: OnboardingItemKind;
  url: string | null;
  responsible: OnboardingResponsible;
  evidence: OnboardingEvidence;
  /** YYYY-MM-DD: start_date + due_days. */
  due_on: string;
  course_id: string | null;
  requires_verification: boolean;
  status: OnboardingItemStatus;
  done_by: string | null;
  done_at: string | null;
  evidence_text: string | null;
  sha256: string | null;
  filename: string | null;
  verified_by: string | null;
  verified_at: string | null;
  /** An admin's note, or the reason an item was marked not applicable (12.3). */
  note: string | null;
  created_at?: string;
  has_evidence_file: boolean;
}

/** An item as an act on it answers (18.24): plus the run's status after the act. */
export interface OnboardingItemResult extends OnboardingRunItem {
  run_status: OnboardingRunStatus;
  /** An external training's file offered against its linked course (12.1). */
  external_cert?: { id: string; status: string };
  /** A course item marked not applicable: the run's own assignments cancelled with it. */
  assignments_cancelled?: number;
}

/** A person as a run names them (common.js personRef). */
export interface OnboardingPersonRef {
  id: string;
  full_name: string | null;
  email: string | null;
  deleted: boolean;
}

export interface OnboardingSiteRef {
  id: string;
  name: string | null;
}

export interface OnboardingTemplateRef {
  id: string;
  name: string | null;
}

/** GET /me/onboarding (18.24). */
export interface MyOnboardingRunView {
  run: OnboardingRun;
  items: OnboardingRunItem[];
  progress: OnboardingProgress;
}

/** GET /admin/onboarding/runs/:id (18.24). */
export interface AdminOnboardingRunView extends MyOnboardingRunView {
  user: OnboardingPersonRef;
  started_by: OnboardingPersonRef;
  site: OnboardingSiteRef;
  template: OnboardingTemplateRef;
}

/** One GET /admin/onboarding/runs row (18.24). */
export interface OnboardingRunListItem extends OnboardingRun {
  user: OnboardingPersonRef;
  template: OnboardingTemplateRef;
  site: OnboardingSiteRef;
  progress: OnboardingProgress;
}

/** POST /admin/onboarding/runs: what happened to the course items (12.2; runs.js syncCourseItems). */
export interface OnboardingStartAssignments {
  already_complete: number;
  assigned: number;
  already_assigned: number;
  failed: number;
}

export interface OnboardingStartResult extends MyOnboardingRunView {
  assignments: OnboardingStartAssignments;
}

/** 5.7 lms_onboarding_templates. */
export interface OnboardingTemplate {
  id: string;
  network_id: string;
  name: string;
  description: string | null;
  is_default: boolean;
  is_active: boolean;
  created_by?: string | null;
  created_at?: string;
  updated_at?: string;
}

/** One GET /admin/onboarding/templates row: the template and its active item count. */
export interface OnboardingTemplateListItem extends OnboardingTemplate {
  item_count: number;
}

/** 5.7 lms_onboarding_items. */
export interface OnboardingTemplateItem {
  id: string;
  template_id: string;
  ordinal: number;
  title: string;
  description_md: string | null;
  kind: OnboardingItemKind;
  url: string | null;
  responsible: OnboardingResponsible;
  evidence: OnboardingEvidence;
  due_days: number;
  course_id: string | null;
  requires_verification: boolean;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

/** GET /admin/onboarding/templates/:id and the items PUT (18.24). */
export interface OnboardingTemplateItems {
  items: OnboardingTemplateItem[];
  retired_items: OnboardingTemplateItem[];
}

export interface OnboardingTemplateDetail extends OnboardingTemplateItems {
  template: OnboardingTemplate;
}

/**
 * One item of the PUT's ordered list (validators.js parseTemplateItem): an
 * existing item carries its id; the list order is the ordinal.
 */
export interface OnboardingTemplateItemInput {
  id?: string;
  title: string;
  description_md: string | null;
  kind: OnboardingItemKind;
  url: string | null;
  responsible: OnboardingResponsible;
  evidence: OnboardingEvidence;
  due_days: number;
  course_id: string | null;
  requires_verification: boolean;
}

// ---------------------------------------------------------------------------
// Error readers (18.24)
// ---------------------------------------------------------------------------

interface ErrorLike {
  response?: { status?: number; data?: Record<string, unknown> };
}

function bodyOf(err: unknown): Record<string, unknown> | null {
  const data = (err as ErrorLike | null | undefined)?.response?.data;
  return data && typeof data === 'object' ? data : null;
}

/** A credential refusal (G14, 5.7): 400 carrying { rule, field }, else null. */
export function credentialProblemOf(err: unknown): { rule: string; field: string } | null {
  const b = bodyOf(err);
  if (!b || typeof b.rule !== 'string' || typeof b.field !== 'string') return null;
  return { rule: b.rule, field: b.field };
}

/** A duplicate template name's 409: existing_template { id, is_active }, else null. */
export function existingTemplateOf(err: unknown): { id: string; is_active: boolean } | null {
  const b = bodyOf(err);
  const t = b?.existing_template as { id?: unknown; is_active?: unknown } | undefined;
  if (!t || typeof t.id !== 'string') return null;
  return { id: t.id, is_active: t.is_active !== false };
}

/** A second active run's 409: active_run_id, else null. */
export function activeRunIdOf(err: unknown): string | null {
  const b = bodyOf(err);
  return b && typeof b.active_run_id === 'string' ? b.active_run_id : null;
}

// ---------------------------------------------------------------------------
// "Mark done" bodies (12.3)
// ---------------------------------------------------------------------------

export interface OnboardingDoneInput {
  /** A written confirmation ('text' items; never a credential, G14). */
  evidenceText?: string | null;
  /** The evidence file ('file' items): a PDF, PNG or JPEG up to 10 MB. */
  file?: File | null;
  /** An external training's certificate dates (12.1), when its file is offered against a course. */
  issuedOn?: string | null;
  expiresOn?: string | null;
}

/**
 * The body of a "Mark done": a FormData when a file rides along (the route
 * runs multer), else plain JSON (multer lets a JSON request through).
 */
export function buildDoneBody(input: OnboardingDoneInput): FormData | { evidence_text?: string } {
  const text = (input.evidenceText ?? '').trim();
  if (input.file) {
    const form = new FormData();
    form.append('file', input.file);
    if (text) form.append('evidence_text', text);
    if (input.issuedOn) form.append('issued_on', input.issuedOn);
    if (input.expiresOn) form.append('expires_on', input.expiresOn);
    return form;
  }
  return text ? { evidence_text: text } : {};
}

// ---------------------------------------------------------------------------
// The hire (12.3)
// ---------------------------------------------------------------------------

/** GET /api/lms/me/onboarding: the caller's active run in this network, or null. */
export async function getMyOnboardingRun(): Promise<MyOnboardingRunView | null> {
  const response = await api.get<MyOnboardingRunView | null>(`${BASE}/me/onboarding`);
  const data = response.data;
  return data && typeof data === 'object' && data.run ? data : null;
}

/** POST /api/lms/me/onboarding/items/:id/done */
export async function markMyItemDone(itemId: string, input: OnboardingDoneInput): Promise<OnboardingItemResult> {
  const response = await api.post<OnboardingItemResult>(`${BASE}/me/onboarding/items/${enc(itemId)}/done`, buildDoneBody(input));
  return response.data;
}

/** POST /api/lms/me/onboarding/items/:id/not-applicable */
export async function markMyItemNotApplicable(itemId: string, reason: string): Promise<OnboardingItemResult> {
  const response = await api.post<OnboardingItemResult>(`${BASE}/me/onboarding/items/${enc(itemId)}/not-applicable`, {
    reason,
  });
  return response.data;
}

export type EvidenceDisposition = 'inline' | 'attachment';

/** GET /api/lms/me/onboarding/items/:id/evidence-url: a presigned URL, valid 10 minutes (2.3a). */
export async function getMyEvidenceUrl(
  itemId: string,
  disposition: EvidenceDisposition = 'inline'
): Promise<{ url: string; filename: string }> {
  const response = await api.get<{ url: string; filename: string }>(`${BASE}/me/onboarding/items/${enc(itemId)}/evidence-url`, {
    params: { disposition },
  });
  return response.data;
}

// ---------------------------------------------------------------------------
// The admin: templates (12.1)
// ---------------------------------------------------------------------------

/** GET /api/lms/admin/onboarding/templates */
export async function listOnboardingTemplates(
  options: { limit?: number; offset?: number } = {}
): Promise<LmsList<OnboardingTemplateListItem>> {
  const params: Record<string, number> = { limit: Math.min(options.limit ?? 200, 200) };
  if (options.offset !== undefined) params.offset = options.offset;
  const response = await api.get<LmsList<OnboardingTemplateListItem>>(`${BASE}/admin/onboarding/templates`, { params });
  return response.data;
}

/** POST /api/lms/admin/onboarding/templates */
export async function createOnboardingTemplate(input: {
  name: string;
  description?: string | null;
  is_default?: boolean;
}): Promise<OnboardingTemplate> {
  const body: Record<string, unknown> = { name: input.name };
  if (input.description) body.description = input.description;
  if (input.is_default) body.is_default = true;
  const response = await api.post<OnboardingTemplate>(`${BASE}/admin/onboarding/templates`, body);
  return response.data;
}

/** GET /api/lms/admin/onboarding/templates/:id */
export async function getOnboardingTemplate(templateId: string): Promise<OnboardingTemplateDetail> {
  const response = await api.get<OnboardingTemplateDetail>(`${BASE}/admin/onboarding/templates/${enc(templateId)}`);
  return response.data;
}

export interface OnboardingTemplatePatch {
  name?: string;
  description?: string | null;
  is_default?: boolean;
  is_active?: boolean;
}

/** PATCH /api/lms/admin/onboarding/templates/:id */
export async function updateOnboardingTemplate(templateId: string, patch: OnboardingTemplatePatch): Promise<OnboardingTemplate> {
  const response = await api.patch<OnboardingTemplate>(`${BASE}/admin/onboarding/templates/${enc(templateId)}`, patch);
  return response.data;
}

/** PUT /api/lms/admin/onboarding/templates/:id/items: the whole ordered list. */
export async function replaceOnboardingTemplateItems(
  templateId: string,
  items: OnboardingTemplateItemInput[]
): Promise<OnboardingTemplateItems> {
  const response = await api.put<OnboardingTemplateItems>(`${BASE}/admin/onboarding/templates/${enc(templateId)}/items`, {
    items,
  });
  return response.data;
}

// ---------------------------------------------------------------------------
// The admin: runs (12.2 to 12.4)
// ---------------------------------------------------------------------------

export interface OnboardingRunQuery {
  status?: OnboardingRunFilter;
  userId?: string | null;
  siteId?: string | null;
  limit?: number;
  offset?: number;
}

/** GET /api/lms/admin/onboarding/runs */
export async function listOnboardingRuns(query: OnboardingRunQuery = {}): Promise<LmsList<OnboardingRunListItem>> {
  const params: Record<string, string | number> = { status: query.status ?? 'active' };
  if (query.userId) params.user_id = query.userId;
  if (query.siteId) params.site_id = query.siteId;
  if (query.limit !== undefined) params.limit = Math.min(query.limit, 200);
  if (query.offset !== undefined) params.offset = query.offset;
  const response = await api.get<LmsList<OnboardingRunListItem>>(`${BASE}/admin/onboarding/runs`, { params });
  return response.data;
}

export interface OnboardingRunStart {
  user_id: string;
  template_id: string;
  site_id: string;
  /** YYYY-MM-DD, the hire's first day. */
  start_date: string;
}

/** POST /api/lms/admin/onboarding/runs (201). */
export async function startOnboardingRun(input: OnboardingRunStart): Promise<OnboardingStartResult> {
  const response = await api.post<OnboardingStartResult>(`${BASE}/admin/onboarding/runs`, input);
  return response.data;
}

/** GET /api/lms/admin/onboarding/runs/:id */
export async function getOnboardingRun(runId: string): Promise<AdminOnboardingRunView> {
  const response = await api.get<AdminOnboardingRunView>(`${BASE}/admin/onboarding/runs/${enc(runId)}`);
  return response.data;
}

/** POST /api/lms/admin/onboarding/runs/:id/cancel */
export async function cancelOnboardingRun(
  runId: string,
  reason: string
): Promise<{ run: OnboardingRun; assignments_cancelled: number }> {
  const response = await api.post<{ run: OnboardingRun; assignments_cancelled: number }>(
    `${BASE}/admin/onboarding/runs/${enc(runId)}/cancel`,
    { reason }
  );
  return response.data;
}

function runItemPath(runId: string, itemId: string): string {
  return `${BASE}/admin/onboarding/runs/${enc(runId)}/items/${enc(itemId)}`;
}

/** POST /api/lms/admin/onboarding/runs/:id/items/:itemId/done (admin items). */
export async function markAdminItemDone(runId: string, itemId: string, input: OnboardingDoneInput): Promise<OnboardingItemResult> {
  const response = await api.post<OnboardingItemResult>(`${runItemPath(runId, itemId)}/done`, buildDoneBody(input));
  return response.data;
}

/** POST /api/lms/admin/onboarding/runs/:id/items/:itemId/not-applicable (any item). */
export async function markAdminItemNotApplicable(runId: string, itemId: string, reason: string): Promise<OnboardingItemResult> {
  const response = await api.post<OnboardingItemResult>(`${runItemPath(runId, itemId)}/not-applicable`, { reason });
  return response.data;
}

/** POST /api/lms/admin/onboarding/runs/:id/items/:itemId/verify */
export async function verifyOnboardingItem(runId: string, itemId: string): Promise<OnboardingItemResult> {
  const response = await api.post<OnboardingItemResult>(`${runItemPath(runId, itemId)}/verify`);
  return response.data;
}

/** PUT /api/lms/admin/onboarding/runs/:id/items/:itemId/note (null clears it). */
export async function setOnboardingItemNote(runId: string, itemId: string, note: string | null): Promise<OnboardingRunItem> {
  const response = await api.put<OnboardingRunItem>(`${runItemPath(runId, itemId)}/note`, { note });
  return response.data;
}

/** GET /api/lms/admin/onboarding/runs/:id/items/:itemId/evidence-url */
export async function getAdminEvidenceUrl(
  runId: string,
  itemId: string,
  disposition: EvidenceDisposition = 'inline'
): Promise<{ url: string; filename: string }> {
  const response = await api.get<{ url: string; filename: string }>(`${runItemPath(runId, itemId)}/evidence-url`, {
    params: { disposition },
  });
  return response.data;
}
